import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  ReactFlow,
  Background,
  MiniMap,
  Node,
  Edge,
  Connection,
  ConnectionMode,
  useNodesState,
  useEdgesState,
  useReactFlow,
  MarkerType,
  ReactFlowProvider,
  NodeChange,
  EdgeChange,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import './OntologyEditorPage.scss';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ConceptNode, ConceptNodeData } from '../components/graph/ConceptNode';
import { FloatingEdge } from '../components/graph/FloatingEdge';
import { NodeDetailsPanel } from '../components/graph/NodeDetailsPanel';
import { NodeBrowserPanel } from '../components/graph/NodeBrowserPanel';
import { EdgeDetailsPanel } from '../components/graph/EdgeDetailsPanel';
import { ResizeHandle } from '../components/layout/ResizeHandle';
import { useResizablePanel } from '@/lib/prefs/useResizablePanel';
import { usePanelResizeNotifier } from '@/lib/prefs/usePanelResizeNotifier';
import { AddPapersModal } from '../components/ontology/AddPapersModal';
import { extendResultToReactFlow } from '../components/graph/extendResultToReactFlow';
import {
  ConceptEdgeData,
  buildConceptEdge,
  applyEdgeDataUpdate,
} from '../components/graph/edge-types';
import { AiToolsPanel } from '../components/ai/AiToolsPanel';
import {
  UnifiedToolbar,
  type LayoutAlgorithm,
  type SaveStatus,
} from '../components/graph/UnifiedToolbar';
import { ConceptLinker } from '../components/graph/ConceptLinker';
import { ConceptExpander } from '../components/graph/ConceptExpander';
import { ExportDialog } from '../components/graph/ExportDialog';
import { PublishVersionDialog } from '../components/graph/PublishVersionDialog';
import { OntologyDetailsDialog } from '../components/graph/OntologyDetailsDialog';
import { OntologySourcesModal } from '../components/graph/OntologySourcesModal';
import { VersionHistoryPanel } from '../components/graph/VersionHistoryPanel';
import { draftLabelFromList } from '@snxt/snxt-onto-share';
import type { ConnectedNodeSuggestion, AddNodeProposal, AddRelationProposal, EditNodeProposal } from '@snxt/snxt-onto-share';
import { useVersions } from '../lib/query/hooks/useVersions';
import { ontologyApi } from '../lib/api/ontology.api';
import { useAuth } from '@/lib/auth/auth-context';

/**
 * Convert generated ontology data from the API into ReactFlow nodes and edges
 */
function convertGeneratedOntologyToGraph(
  generatedNodes: any[],
  generatedEdges: any[],
  edgeColor: string
): { nodes: Node[]; edges: Edge[] } {
  // Position nodes in a circular layout initially
  const nodeCount = generatedNodes.length;
  const radius = Math.max(300, nodeCount * 30);
  const angleStep = (2 * Math.PI) / nodeCount;

  const nodes: Node[] = generatedNodes.map((node, index) => {
    const angle = index * angleStep - Math.PI / 2; // Start from top
    return {
      id: node.id,
      type: 'concept',
      position: {
        x: 500 + radius * Math.cos(angle),
        y: 400 + radius * Math.sin(angle),
      },
      data: {
        label: node.label,
        definition: node.definition || 'No definition provided',
        properties: node.properties || [],
      },
    };
  });

  const edges: Edge[] = generatedEdges.map((edge) =>
    buildConceptEdge({
      id: edge.id,
      source: edge.source,
      target: edge.target,
      edgeColor,
      data: {
        label: edge.label || edge.relationshipType || 'relates to',
        relationshipType: edge.relationshipType || edge.label || 'relates-to',
        description: edge.description || '',
        properties: Array.isArray(edge.properties) ? edge.properties : [],
        // A9/A10: generated relationships are AI-origin and carry the AI's rationale.
        origin: 'ai',
        rationale: edge.rationale || '',
      },
    }),
  );

  return { nodes, edges };
}

const defaultNodes: Node[] = [
  {
    id: 'center',
    type: 'concept',
    position: { x: 400, y: 300 },
    data: { 
      label: 'New Concept',
      definition: 'Click to edit this concept...',
      properties: []
    },
  },
];

const defaultEdges: Edge[] = [];

/**
 * How long the AI panel takes to animate its width.
 *
 * MUST stay in sync with the `transition: width 0.3s` on `.ai-panel` in OntologyEditorPage.scss:
 * the canvas is re-measured once this elapses, and measuring early reads the width the panel is
 * animating away from, which is what left a dead band beside the graph (2026-09-03).
 */
const AI_PANEL_TRANSITION_MS = 300;

type OntologyEditorPageProps = {
  theme: 'g10' | 'g100';
  onToggleTheme: () => void;
};

/**
 * OntologyEditorPageContent Component
 *
 * Main ontology editor with interactive graph visualization.
 * Uses ReactFlow for node-based editing with IBM Carbon design integration.
 * 
 * Layout: Unified Toolbar (top) | Left Inspector | Canvas | Right AI Panel
 */
function OntologyEditorPageContent({ theme, onToggleTheme }: OntologyEditorPageProps) {
  // Current authenticated user — used to stamp provenance (created/updated by)
  // on new or human-edited nodes. Kept in a ref so callbacks that close over
  // it never go stale.
  const { user } = useAuth();
  const userRef = useRef<typeof user>(null);
  useEffect(() => { userRef.current = user; }, [user]);

  const location = useLocation();
  const navigate = useNavigate();
  const { id: routeOntologyId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  // Step C: when this is set, the editor loads the *published snapshot* of
  // the requested version (read-only) instead of the live draft. The id
  // refers to a row in `ontology_versions` that belongs to this ontology.
  const viewingVersionId = searchParams.get('version');
  const locationState = location.state as {
    generatedOntology?: any;
    nodes?: any[];
    edges?: any[];
    sourceDocumentIds?: string[];
  } | null;

  // The currently *active* ontology id. Seeded from the URL, but updated after
  // every save because `POST /api/ontologies/save` deletes the previous row
  // and inserts a fresh one with a brand-new UUID. Without this, the chat
  // panel would keep querying the old (now-missing) ontology id and either
  // 404 or — worse — retrieve from a stale row, which is the
  // "chat reuses a previous version" symptom we hit after saving.
  // Only treat the route param as an ontology ID if it looks like a valid UUID.
  // Values like "new" are used as navigation markers and must not trigger a fetch.
  const isValidUUIDParam = (id?: string) => !!id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
  const [ontologyId, setOntologyId] = useState<string | undefined>(
    isValidUUIDParam(routeOntologyId) ? routeOntologyId : undefined
  );

  // Keep local state in sync if the user navigates between editor URLs
  // (e.g. via the home page) without a full reload.
  useEffect(() => {
    setOntologyId(isValidUUIDParam(routeOntologyId) ? routeOntologyId : undefined);
  }, [routeOntologyId]);

  // Determine initial edge color based on theme
  const initialEdgeColor = theme === 'g100' ? '#ffffff' : '#161616';

  // Initialize with generated ontology data if available, otherwise use defaults
  const getInitialData = (): { nodes: Node[]; edges: Edge[] } => {
    if (locationState?.nodes && locationState.nodes.length > 0) {
      return convertGeneratedOntologyToGraph(
        locationState.nodes,
        locationState.edges || [],
        initialEdgeColor
      );
    }
    // If we have an ID but no location state, start empty (will be loaded via effect)
    if (ontologyId) {
      return { nodes: [], edges: [] };
    }
    return { nodes: defaultNodes, edges: defaultEdges };
  };

  const initialData = getInitialData();

  const [nodes, setNodes, onNodesChangeRaw] = useNodesState(initialData.nodes);
  const [edges, setEdges, onEdgesChangeRaw] = useEdgesState(initialData.edges);
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const [selectedNodes, setSelectedNodes] = useState<Node[]>([]);
  // The currently inspected edge (relationship). Mutually exclusive with
  // `selectedNode`: opening one closes the other so the left inspector drawer
  // can host either form without conflict.
  const [selectedEdge, setSelectedEdge] = useState<Edge | null>(null);
  const [currentLayout, setCurrentLayout] = useState<LayoutAlgorithm>('force');
  const [isApplyingLayout, setIsApplyingLayout] = useState(false);
  const [isLinkerOpen, setIsLinkerOpen] = useState(false);
  const [linkSourceNode, setLinkSourceNode] = useState<Node | null>(null);
  const [isExpanderOpen, setIsExpanderOpen] = useState(false);
  const [expandSourceNode, setExpandSourceNode] = useState<Node | null>(null);
  // (Save dialog removed in Step C — Save is now silent / non-modal.)
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false);
  const [isSavingDetails, setIsSavingDetails] = useState(false);
  const [isSourcesOpen, setIsSourcesOpen] = useState(false);
  const [isPublishDialogOpen, setIsPublishDialogOpen] = useState(false);
  const [isVersionHistoryOpen, setIsVersionHistoryOpen] = useState(false);
  // The version label shown in the toolbar pill. Derived from the version graph,
  // never invented: a draft has no number (migration 043), so it reads
  // "Draft (based on vX)", and a published snapshot reads "vX". Computed below,
  // once `viewingSnapshot` and the version list are known.
  // Whether this ontology is being viewed in shared/read-only mode (the user
  // is not the owner). Set during the load effect from the API response.
  const [isReadOnly, setIsReadOnly] = useState(false);
  // Snapshot info when viewing a historical published version (Step C).
  // Mirrors the `viewing_version` field returned by the API. When non-null,
  // `isReadOnly` is also forced true so the canvas behaves like a shared
  // ontology (no edits allowed).
  const [viewingSnapshot, setViewingSnapshot] = useState<{
    id: string;
    version_number: string;
    published_at: string | null;
    release_notes: string | null;
  } | null>(null);
  // True only while the initial ontology fetch is in flight. Drives the
  // "Loading ontology…" placeholder over the canvas.
  const [isLoadingOntology, setIsLoadingOntology] = useState(false);

  // The version graph, so the toolbar pill can name the draft by its lineage.
  const versionsQuery = useVersions(ontologyId);
  const versionLabelText = useMemo(() => {
    // Viewing a frozen published snapshot: the API already resolved its number.
    if (viewingSnapshot) return `v${viewingSnapshot.version_number}`;
    const all = versionsQuery.data ?? [];
    const draft = all.find((v) => v.status === 'draft');
    // Undefined (not a fabricated number) while the list is loading, so the pill
    // simply does not render yet.
    if (!draft) return undefined;
    return draftLabelFromList(draft, all);
  }, [viewingSnapshot, versionsQuery.data]);

  // Save button feedback. Driven by `handleSave`: flips to `'saving'` while
  // the network request is in flight, then `'saved'` (briefly) on success
  // or `'error'` on failure. We keep the ref alongside so we can clear a
  // pending revert-to-`idle` timer if the user clicks Save again before
  // the previous animation finishes.
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const saveStatusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // True once the user has made graph changes that haven't been saved yet.
  // Drives the "Unsaved changes" banner shown below the toolbar.
  const [isDirty, setIsDirty] = useState(false);
  // Stable callback used by every mutation handler below.
  // Selection, dimension, and focusNode changes must NOT call this.
  const markDirty = useCallback(() => {
    if (!isReadOnly) setIsDirty(true);
  }, [isReadOnly]);

  // Wrap node/edge change handlers to block destructive changes in read-only mode.
  const onNodesChange = useCallback(
    (changes: NodeChange[]) => {
      if (!isReadOnly) {
        onNodesChangeRaw(changes);
        return;
      }
      // Filter out removes and position changes (drags) in read-only mode
      const allowed = changes.filter(
        (c) => c.type !== 'remove' && c.type !== 'position',
      );
      if (allowed.length > 0) onNodesChangeRaw(allowed);
    },
    [isReadOnly, onNodesChangeRaw],
  );

  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) => {
      if (!isReadOnly) {
        onEdgesChangeRaw(changes);
        return;
      }
      // Filter out removes in read-only mode
      const allowed = changes.filter((c) => c.type !== 'remove');
      if (allowed.length > 0) onEdgesChangeRaw(allowed);
    },
    [isReadOnly, onEdgesChangeRaw],
  );
  const [ontologyName, setOntologyName] = useState<string>(
    locationState?.generatedOntology?.name || 'Untitled Ontology'
  );
  const [ontologyDescription, setOntologyDescription] = useState<string>(
    locationState?.generatedOntology?.description || ''
  );
  // A17: KB documents this ontology was generated from (Start from KB). Persisted
  // as sources on the first save so the editor can show "what created this".
  const [sourceDocumentIds] = useState<string[]>(locationState?.sourceDocumentIds || []);
  
  // A7: persistent, draggable widths for the two side panels.
  // The AI panel can grow to 50% of the viewport width.
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);
  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const inspectorResize = useResizablePanel({
    storageKey: 'snxt-onto-inspector-width',
    min: 260,
    max: 640,
    edge: 'right',
    defaultWidth: 340,
  });
  const aiResize = useResizablePanel({
    storageKey: 'snxt-onto-ai-panel-width',
    min: 300,
    max: Math.max(760, Math.floor(viewportWidth * 0.5)),
    edge: 'left',
    defaultWidth: 380,
  });

  // Panel states
  const [isInspectorOpen, setIsInspectorOpen] = useState(false);
  const [isNodeBrowserOpen, setIsNodeBrowserOpen] = useState(false);
  const [addPapersOpen, setAddPapersOpen] = useState(false); // A1: add-papers modal
  const [isAiPanelOpen, setIsAiPanelOpen] = useState(() => {
    // Persist AI panel state in localStorage
    const saved = localStorage.getItem('snxt-onto-ai-panel-open');
    return saved !== null ? saved === 'true' : true;
  });

  const [edgeColor, setEdgeColor] = useState(initialEdgeColor);
  // True while the user is mid-drag of a new connection. We use this to
  // toggle a class on the editor root so all node handles light up,
  // making it obvious where you can drop.
  const [isConnecting, setIsConnecting] = useState(false);

  // Ref to track current nodes without triggering callback re-creation
  const nodesRef = useRef(nodes);
  useEffect(() => {
    nodesRef.current = nodes;
  }, [nodes]);

  // When set true by the load effect, triggers a one-shot force layout once
  // the node/edge state has actually landed in React state.
  const autoLayoutPendingRef = useRef(false);

  const { fitView, zoomIn, zoomOut, setCenter, getNode } = useReactFlow();

  /**
   * Focus a node on the canvas — used when the user clicks a citation chip
   * in the chat panel. Smoothly recenters and pulses the node selection.
   */
  const focusNode = useCallback(
    (nodeId: string) => {
      const node = getNode(nodeId);
      if (!node) return;
      const x = (node.position?.x ?? 0) + (node.measured?.width ?? node.width ?? 200) / 2;
      const y = (node.position?.y ?? 0) + (node.measured?.height ?? node.height ?? 80) / 2;
      setCenter(x, y, { zoom: 1.2, duration: 600 });
      setNodes((prev) =>
        prev.map((n) => ({ ...n, selected: n.id === nodeId })),
      );
    },
    [getNode, setCenter, setNodes],
  );

  // Persist AI panel state
  useEffect(() => {
    localStorage.setItem('snxt-onto-ai-panel-open', String(isAiPanelOpen));
  }, [isAiPanelOpen]);

  // Reclaim the canvas width when the AI panel collapses (and give it back when it opens).
  // Closing the chat used to leave a dead black band where the panel had been, because <ReactFlow>
  // only measures its container on mount. See usePanelResizeNotifier for the full measurement, and
  // why this is a resize rather than a fitView (fitView would discard the user's zoom and pan).
  // The duration MUST match the `.ai-panel` width transition in OntologyEditorPage.scss.
  usePanelResizeNotifier(isAiPanelOpen, AI_PANEL_TRANSITION_MS);

  // Open/close inspector when a node OR an edge becomes selected.
  // Also close the node browser when the user clicks directly on the canvas,
  // keeping the two left-panel views mutually exclusive.
  useEffect(() => {
    if (selectedNode || selectedEdge) {
      setIsInspectorOpen(true);
      setIsNodeBrowserOpen(false);
    }
  }, [selectedNode, selectedEdge]);

  // Load ontology from backend when ID is present and looks like a valid UUID
  useEffect(() => {
    if (!ontologyId || locationState?.nodes || locationState?.generatedOntology) return;
    if (!isValidUUIDParam(ontologyId)) return;

    const loadOntology = async () => {
      setIsLoadingOntology(true);
      try {
        // Fetch ontology with nodes and edges in one call.
        // Step C: when ?version=<id> is in the URL, the backend returns the
        // immutable snapshot for that published version (and forces
        // is_shared:true so we render in read-only mode).
        const ontologyData = await ontologyApi.getOntology(ontologyId);

        setOntologyName(ontologyData.name || 'Untitled Ontology');
        setOntologyDescription(ontologyData.description || '');
        // A13: owner + write-shared can edit (can_write); read-only otherwise.
        // Fall back to is_shared for responses predating the can_write field.
        setIsReadOnly(
          ontologyData.can_write === undefined
            ? !!ontologyData.is_shared
            : !ontologyData.can_write,
        );
        setViewingSnapshot(ontologyData.viewing_version || null);

        const nodesData = ontologyData.nodes || [];
        const edgesData = ontologyData.edges || [];

        // Convert backend nodes to ReactFlow nodes
        const resolvedColor = theme === 'g100' ? '#ffffff' : '#161616';
        const rfNodes: Node[] = nodesData.map((n: any) => {
          const isLocked = n.metadata?.locked || false;
          return {
            id: n.node_id || n.id,
            type: 'concept',
            draggable: !isLocked,
            position: {
              x: n.position_x ?? n.positionX ?? 0,
              y: n.position_y ?? n.positionY ?? 0,
            },
            data: {
              label: n.label,
              definition: n.definition || '',
              properties: n.properties || [],
              locked: isLocked,
              origin: n.metadata?.origin === 'user' ? 'user' : 'ai',
              // Provenance columns (migration 039) — first-class on the node.
              provenance: n.provenance ?? 'ai',
              createdByName: n.created_by_name ?? null,
              updatedByName: n.updated_by_name ?? null,
              correctedAt: n.corrected_at ? String(n.corrected_at) : null,
            },
          };
        });

        // Convert backend edges to ReactFlow edges (with full data payload)
        const rfEdges: Edge[] = edgesData.map((e: any) => {
          const properties =
            (Array.isArray(e.properties)
              ? e.properties
              : e.properties && typeof e.properties === 'object'
              ? Object.entries(e.properties).map(([name, type]) => ({
                  name,
                  type: typeof type === 'string' ? type : undefined,
                }))
              : []) as ConceptEdgeData['properties'];

          return buildConceptEdge({
            id: e.edge_id || e.id,
            source: e.source_node_id || e.sourceNodeId,
            target: e.target_node_id || e.targetNodeId,
            edgeColor: resolvedColor,
            data: {
              label:
                e.label ||
                e.relationship_type ||
                e.relationshipType ||
                'relates to',
              relationshipType:
                e.relationship_type || e.relationshipType || e.label || 'relates-to',
              description: e.description || e.metadata?.description || '',
              properties,
              bidirectional: !!(e.metadata?.bidirectional ?? e.bidirectional),
              origin: e.metadata?.origin === 'user' ? 'user' : 'ai',
              // Provenance columns (migration 039) — carried but not badged.
              provenance: e.provenance ?? 'ai',
              createdByName: e.created_by_name ?? null,
              updatedByName: e.updated_by_name ?? null,
              correctedAt: e.corrected_at ? String(e.corrected_at) : null,
              rationale: e.metadata?.rationale || e.rationale || '',
            },
          });
        });

        setNodes(rfNodes);
        setEdges(rfEdges);
        // If no node has a non-zero saved position, run force-directed layout
        // so the graph is readable immediately (avoids the "all nodes at 0,0" pile).
        const hasLayout = rfNodes.some(
          (n) => n.position.x !== 0 || n.position.y !== 0
        );
        if (!hasLayout && rfNodes.length > 0) {
          // Signal to run force layout once nodes are in state (see effect below).
          autoLayoutPendingRef.current = true;
        } else {
          setTimeout(() => {
            fitView({ padding: 0.2, duration: 800 });
          }, 300);
        }
      } catch (error) {
        console.error('Failed to load ontology:', error);
      } finally {
        setIsLoadingOntology(false);
      }
    };

    loadOntology();
    // Re-run when the URL switches between ?version=<id> and the live draft
    // (or between two different version ids) so the canvas swaps content.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- a theme change must not reload the graph
  }, [ontologyId, viewingVersionId]);

  // Fit view after initial load with generated ontology
  useEffect(() => {
    if (locationState?.nodes && locationState.nodes.length > 0) {
      setTimeout(() => {
        fitView({ padding: 0.2, duration: 800 });
      }, 200);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the initial fit happens once, by design
  }, []); // Run once on mount

  useEffect(() => {
    const resolvedColor = theme === 'g100' ? '#ffffff' : '#161616';
    setEdgeColor(resolvedColor);
    setEdges((currentEdges) =>
      currentEdges.map((edge) => ({
        ...edge,
        type: 'floating',
        animated: false,
        style: { ...edge.style, stroke: resolvedColor, strokeWidth: 2.5 },
        markerEnd: { type: MarkerType.ArrowClosed, color: resolvedColor },
      }))
    );
  }, [theme, setEdges]);

  // Unified selection handler — tracks both nodes and edges so the left
  // inspector can display whichever is the most recent single selection.
  const onSelectionChange = useCallback(
    ({ nodes: selNodes, edges: selEdges }: { nodes: Node[]; edges: Edge[] }) => {
      setSelectedNodes(selNodes);

      // Single node selected → show node panel
      if (selNodes.length === 1 && selEdges.length === 0) {
        setSelectedNode(selNodes[0]);
        setSelectedEdge(null);
        return;
      }
      // Single edge selected → show edge panel
      if (selEdges.length === 1 && selNodes.length === 0) {
        setSelectedEdge(selEdges[0]);
        setSelectedNode(null);
        return;
      }
      // Empty selection → clear both
      if (selNodes.length === 0 && selEdges.length === 0) {
        setSelectedNode(null);
        setSelectedEdge(null);
      }
      // Multi-selection: keep panels closed for now
    },
    [],
  );

  // Lifecycle hooks for the drag-to-connect gesture. They drive the
  // `isConnecting` flag which the SCSS uses to reveal handles on every
  // node (so the user can see valid drop targets across the canvas).
  const onConnectStart = useCallback(() => {
    setIsConnecting(true);
  }, []);
  const onConnectEnd = useCallback(() => {
    setIsConnecting(false);
  }, []);

  // Handle new connections between nodes (drag-to-link).
  // Uses `buildConceptEdge` so the new relationship gets the canonical
  // data payload, including a default label/type the user can edit
  // immediately afterwards via the edge inspector.
  const onConnect = useCallback(
    (connection: Connection) => {
      if (isReadOnly) return;
      if (!connection.source || !connection.target) return;
      // Disallow self-loops — they're rarely meaningful in ontologies and
      // tend to confuse layout algorithms.
      if (connection.source === connection.target) return;

      const newEdge = buildConceptEdge({
        id: `edge-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        source: connection.source,
        target: connection.target,
        edgeColor,
      });
      setEdges((eds) => [...eds, newEdge]);
      markDirty();
      // Auto-open the inspector on the new edge so the user can name it.
      setSelectedEdge(newEdge);
      setSelectedNode(null);
    },
    [edgeColor, setEdges, isReadOnly, markDirty],
  );

  // Validate a candidate connection. Self-loops are still rejected
  // (rarely meaningful in ontologies and confusing for layouts), but we
  // intentionally ALLOW multiple edges between the same pair of nodes —
  // FloatingEdge fans them out into curved parallel arcs so each
  // relationship stays individually clickable and editable.
  const isValidConnection = useCallback(
    (conn: Connection | Edge) => {
      if (!conn.source || !conn.target) return false;
      if (conn.source === conn.target) return false;
      return true;
    },
    [],
  );

  // Update an existing edge with a patched data payload. We rebuild the
  // visible label/markers via `applyEdgeDataUpdate` so the canvas stays in
  // sync with what the inspector saved.
  const handleUpdateEdge = useCallback(
    (edgeId: string, patch: Partial<ConceptEdgeData>) => {
      const userName =
        userRef.current?.display_name || userRef.current?.email || null;
      const now = new Date().toISOString();
      // A10: editing a relationship in the inspector is a user correction — mark
      // it origin='user' so it's visually distinct from the AI's original, and
      // record provenance (ai → corrected once, keeping user/corrected as-is).
      const markPatch = (edge: Edge): Partial<ConceptEdgeData> => {
        const existing = (edge.data ?? {}) as ConceptEdgeData;
        const currentProvenance = existing.provenance ?? 'ai';
        const nextProvenance = currentProvenance === 'ai' ? 'corrected' : currentProvenance;
        const correctedAt =
          nextProvenance === 'corrected' && !existing.correctedAt ? now : existing.correctedAt ?? null;
        return {
          ...patch,
          origin: 'user',
          provenance: nextProvenance,
          updatedByName: userName,
          correctedAt,
          // Mark as unsaved so the canvas edge renders with a dashed warning
          // stroke until the next successful handleSave round-trip clears it.
          unsaved: true,
        };
      };
      setEdges((eds) =>
        eds.map((edge) =>
          edge.id === edgeId ? applyEdgeDataUpdate(edge, markPatch(edge), edgeColor) : edge,
        ),
      );
      markDirty();
      // Reflect the change in the currently-inspected edge state so the
      // panel's "Saved" button label updates immediately.
      setSelectedEdge((prev) => {
        if (!prev || prev.id !== edgeId) return prev;
        return applyEdgeDataUpdate(prev, markPatch(prev), edgeColor);
      });
    },
    [edgeColor, setEdges, markDirty],
  );

  const handleDeleteEdge = useCallback(
    (edgeId: string) => {
      setEdges((eds) => eds.filter((e) => e.id !== edgeId));
      setSelectedEdge((prev) => (prev?.id === edgeId ? null : prev));
      markDirty();
    },
    [setEdges, markDirty],
  );

  // Double-click on an edge → open inspector for that relationship.
  // Useful as a fallback to single-click selection on dense graphs.
  const onEdgeDoubleClick = useCallback(
    (_event: React.MouseEvent, edge: Edge) => {
      setSelectedEdge(edge);
      setSelectedNode(null);
      setIsInspectorOpen(true);
    },
    [],
  );

  // Update node data. A content edit (label/definition/properties) by a human
  // records provenance: an AI-generated node flips 'ai' → 'corrected' and stamps
  // correctedAt once; nodes already 'user'/'corrected' keep their state and just
  // refresh updatedByName. Pure lock/unlock updates don't count as corrections.
  const handleUpdateNode = useCallback((nodeId: string, data: Partial<ConceptNodeData>) => {
    const currentUser = userRef.current;
    const userName = currentUser?.display_name || currentUser?.email || null;
    const now = new Date().toISOString();
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id !== nodeId) return node;
        const isContentEdit = 'label' in data || 'definition' in data || 'properties' in data;
        if (!isContentEdit) {
          return { ...node, data: { ...node.data, ...data } };
        }
        const existing = node.data as ConceptNodeData;
        const currentProvenance = existing.provenance ?? 'ai';
        const nextProvenance = currentProvenance === 'ai' ? 'corrected' : currentProvenance;
        // Set correctedAt only on the first ai → corrected transition.
        const correctedAt =
          nextProvenance === 'corrected' && !existing.correctedAt ? now : existing.correctedAt ?? null;
        return {
          ...node,
          data: {
            ...existing,
            ...data,
            origin: 'user' as const,
            provenance: nextProvenance,
            updatedByName: userName,
            correctedAt,
          },
        };
      })
    );
    markDirty();
  }, [setNodes, markDirty]);

  // Add new concept
  const handleAddConcept = useCallback(() => {
    const newNode: Node = {
      id: `node-${Date.now()}`,
      type: 'concept',
      position: {
        x: Math.random() * 500 + 200,
        y: Math.random() * 500 + 100
      },
      data: {
        label: 'New Concept',
        definition: 'Define this concept...',
        properties: [],
        origin: 'user',
        // Human explicitly created this concept → provenance 'user'.
        provenance: 'user',
        createdByName:
          userRef.current?.display_name || userRef.current?.email || null,
        updatedByName: null,
        correctedAt: null,
      },
    };
    setNodes((nds) => [...nds, newNode]);
    markDirty();
  }, [setNodes, markDirty]);

  // Expand concept with AI
  const handleExpandConcept = useCallback((nodeId: string) => {
    const node = nodesRef.current.find(n => n.id === nodeId);
    if (node) {
      setExpandSourceNode(node);
      setIsExpanderOpen(true);
    }
  }, []);

  // Link concepts with AI
  const handleLinkConcept = useCallback((nodeId: string) => {
    const node = nodesRef.current.find(n => n.id === nodeId);
    if (node) {
      setLinkSourceNode(node);
      setIsLinkerOpen(true);
    }
  }, []);

  // Create link between concepts (from ConceptLinker modal)
  const handleCreateLink = useCallback(
    (sourceId: string, targetId: string, relationship: string) => {
      const newEdge = buildConceptEdge({
        id: `edge-${Date.now()}`,
        source: sourceId,
        target: targetId,
        edgeColor,
        data: {
          label: relationship,
          relationshipType: relationship,
          origin: 'user',
          provenance: 'user',
          createdByName:
            userRef.current?.display_name || userRef.current?.email || null,
          updatedByName: null,
          correctedAt: null,
        },
      });
      setEdges((eds) => [...eds, newEdge]);
      markDirty();
    },
    [edgeColor, setEdges, markDirty],
  );

  // Add AI-accepted edges from the "Connect Disconnected Nodes" action.
  const handleAddAiEdges = useCallback(
    (suggestions: ConnectedNodeSuggestion[]) => {
      const newEdges = suggestions.map((suggestion, index) => {
        const [source, target] =
          suggestion.direction === 'to-isolated'
            ? [suggestion.targetNodeId, suggestion.isolatedNodeId]
            : [suggestion.isolatedNodeId, suggestion.targetNodeId];

        return buildConceptEdge({
          id: `edge-${Date.now()}-${index}`,
          source,
          target,
          edgeColor,
          data: {
            label: suggestion.label,
            relationshipType: suggestion.relationshipType,
            origin: 'ai',
            provenance: 'ai',
            rationale: suggestion.rationale,
          },
        });
      });
      setEdges((eds) => [...eds, ...newEdges]);
      markDirty();
    },
    [edgeColor, setEdges, markDirty],
  );

  // ─── Co-Ontologist proposal handlers ──────────────────────────────────────

  /** Accept an add_node proposal from the Co-Ontologist chat. */
  const handleAddAiNode = useCallback(
    (proposal: AddNodeProposal) => {
      const newNodeId = `node-${Date.now()}`;
      const newNode: Node = {
        id: newNodeId,
        type: 'concept',
        position: {
          x: Math.random() * 500 + 200,
          y: Math.random() * 500 + 100,
        },
        data: {
          label: proposal.label,
          definition: proposal.description || '',
          properties: (proposal.properties ?? []).map((p) => ({ name: p.name, type: p.value })),
          origin: 'ai' as const,
          provenance: 'ai' as const,
          createdByName: null,
          updatedByName: null,
          correctedAt: null,
        } satisfies ConceptNodeData,
      };
      setNodes((nds) => [...nds, newNode]);
      markDirty();
      // React Flow processes the node state update asynchronously — defer
      // focus/selection so getNode can find the newly-added node.
      setTimeout(() => {
        focusNode(newNodeId);
        const added = getNode(newNodeId);
        if (added) {
          setSelectedNode(added);
          setSelectedEdge(null);
        }
      }, 0);
    },
    [focusNode, getNode, setNodes, markDirty],
  );

  /** Accept an add_relation proposal from the Co-Ontologist chat.
   *
   * The LLM invents placeholder node IDs — it has no knowledge of the real
   * React Flow graph IDs. Resolve source and target by matching the proposal's
   * node labels against the current graph nodes before creating the edge.
   * Falls back to the proposal's own IDs only if no label match is found, so
   * the edge is still created (dangling) rather than silently dropped.
   */
  const handleAddAiEdge = useCallback(
    (proposal: AddRelationProposal) => {
      const findNodeIdByLabel = (label: string): string => {
        const match = nodes.find(
          (n) => (n.data as { label?: string }).label?.toLowerCase() === label.toLowerCase(),
        );
        return match?.id ?? label; // fall back to the label string rather than the invented ID
      };

      const sourceId = findNodeIdByLabel(proposal.sourceNodeLabel);
      const targetId = findNodeIdByLabel(proposal.targetNodeLabel);

      const newEdge = buildConceptEdge({
        id: `edge-${Date.now()}`,
        source: sourceId,
        target: targetId,
        edgeColor,
        data: {
          label: proposal.label,
          relationshipType: proposal.relationshipType,
          origin: 'ai' as const,
          provenance: 'ai' as const,
          rationale: proposal.rationale,
        },
      });
      setEdges((eds) => [...eds, newEdge]);
      markDirty();
    },
    [nodes, edgeColor, setEdges, markDirty],
  );

  /** Accept an edit_node proposal from the Co-Ontologist chat.
   *  Delegates to handleUpdateNode which applies provenance tracking.
   *  Proposed properties are merged with the existing properties list —
   *  existing entries with the same name are overwritten, new ones are appended. */
  const handleUpdateNodeFromProposal = useCallback(
    (proposal: EditNodeProposal) => {
      const patch: Partial<ConceptNodeData> = {};
      if (proposal.label) patch.label = proposal.label;
      if (proposal.description !== undefined) patch.definition = proposal.description;
      if (proposal.properties && proposal.properties.length > 0) {
        const existing = (nodes.find((n) => n.id === proposal.nodeId)?.data as ConceptNodeData)?.properties ?? [];
        // Normalise existing entries to {name, type} objects for comparison
        const normalised = existing.map((p) =>
          typeof p === 'string' ? { name: p, type: undefined } : p,
        );
        const incoming = proposal.properties.map((p) => ({ name: p.name, type: p.value }));
        // Merge: replace by name, then append new
        const merged = [...normalised];
        for (const inc of incoming) {
          const idx = merged.findIndex((e) => e.name === inc.name);
          if (idx >= 0) merged[idx] = inc;
          else merged.push(inc);
        }
        patch.properties = merged;
      }
      handleUpdateNode(proposal.nodeId, patch);
    },
    [nodes, handleUpdateNode],
  );

  // Add concepts from AI expansion
  const handleAddConcepts = useCallback((
    concepts: ConceptNodeData[],
    relationships: Array<{ source: string; target: string; label: string }>
  ) => {
    const sourceNode = expandSourceNode;
    if (!sourceNode) return;

    const baseX = sourceNode.position.x;
    const baseY = sourceNode.position.y;
    const radius = 200;
    const angleStep = (2 * Math.PI) / concepts.length;

    const newNodes: Node[] = concepts.map((concept, index) => {
      const angle = index * angleStep;
      return {
        id: relationships[index].target,
        type: 'concept',
        position: {
          x: baseX + radius * Math.cos(angle),
          y: baseY + radius * Math.sin(angle),
        },
        data: concept,
      };
    });

    const newEdges: Edge[] = relationships.map((rel) =>
      buildConceptEdge({
        id: `edge-${Date.now()}-${rel.target}`,
        source: rel.source,
        target: rel.target,
        edgeColor,
        data: {
          label: rel.label,
          relationshipType: rel.label,
        },
      }),
    );

    setNodes((nds) => [...nds, ...newNodes]);
    setEdges((eds) => [...eds, ...newEdges]);
    markDirty();
  }, [expandSourceNode, edgeColor, setNodes, setEdges, markDirty]);

  // A1: merge an "add papers" extension (new deduped nodes/edges) into the
  // canvas, laid out below the current graph. Persisted on the next Save
  // (handleSave reads nodes/edges state). Edges may reference existing node ids.
  const handleExtendResult = useCallback(
    (result: {
      nodes: Array<{ id: string; label: string; definition: string; properties: any[] }>;
      edges: Array<{ id: string; source: string; target: string; label: string; relationshipType: string }>;
    }) => {
      const { nodes: addNodes, edges: addEdges } = extendResultToReactFlow(result, nodes, edgeColor);
      setNodes((nds) => [...nds, ...addNodes]);
      setEdges((eds) => [...eds, ...addEdges]);
      markDirty();
    },
    [nodes, edgeColor, setNodes, setEdges, markDirty],
  );

  // Delete node and associated relationships
  const handleDeleteNode = useCallback((nodeId: string) => {
    setNodes((nds) => nds.filter((node) => node.id !== nodeId));
    setEdges((eds) => eds.filter((edge) => edge.source !== nodeId && edge.target !== nodeId));
    setSelectedNode((prev) => (prev?.id === nodeId ? null : prev));
    markDirty();
  }, [setNodes, setEdges, markDirty]);

  // Lock node
  const handleLockNode = useCallback((nodeId: string) => {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === nodeId) {
          return {
            ...node,
            draggable: false,
            data: { ...node.data, locked: true },
          };
        }
        return node;
      })
    );
    markDirty();
  }, [setNodes, markDirty]);

  // Unlock node
  const handleUnlockNode = useCallback((nodeId: string) => {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === nodeId) {
          return {
            ...node,
            draggable: true,
            data: { ...node.data, locked: false },
          };
        }
        return node;
      })
    );
    markDirty();
  }, [setNodes, markDirty]);

  // Lock all nodes
  const handleLockAll = useCallback(() => {
    setNodes((nds) =>
      nds.map((node) => ({
        ...node,
        draggable: false,
        data: { ...node.data, locked: true },
      }))
    );
    markDirty();
  }, [setNodes, markDirty]);

  // Unlock all nodes
  const handleUnlockAll = useCallback(() => {
    setNodes((nds) =>
      nds.map((node) => ({
        ...node,
        draggable: true,
        data: { ...node.data, locked: false },
      }))
    );
    markDirty();
  }, [setNodes, markDirty]);

  // Apply layout algorithm
  const handleApplyLayout = useCallback((algorithm: LayoutAlgorithm, spacing: number = 50) => {
    setIsApplyingLayout(true);
    setCurrentLayout(algorithm);
    
    setTimeout(() => {
      setNodes((nds) => {
        const layoutedNodes = nds.map(n => ({ ...n, position: { ...n.position } }));
        const nodeCount = layoutedNodes.length;
        
        if (nodeCount === 0) return layoutedNodes;
        
        const isNodeLocked = (node: typeof layoutedNodes[0]) => {
          return (node.data as ConceptNodeData)?.locked === true;
        };
        
        switch (algorithm) {
          case 'circular': {
            const unlockedNodes = layoutedNodes.filter(n => !isNodeLocked(n));
            const unlockedCount = unlockedNodes.length;
            if (unlockedCount === 0) break;
            
            const radius = Math.max(300, nodeCount * 40);
            const angleStep = (2 * Math.PI) / unlockedCount;
            const centerX = 500;
            const centerY = 400;
            unlockedNodes.forEach((node, index) => {
              const angle = index * angleStep - Math.PI / 2;
              node.position = {
                x: centerX + radius * Math.cos(angle),
                y: centerY + radius * Math.sin(angle),
              };
            });
            break;
          }
            
          case 'grid': {
            const unlockedNodes = layoutedNodes.filter(n => !isNodeLocked(n));
            const unlockedCount = unlockedNodes.length;
            if (unlockedCount === 0) break;
            
            const cols = Math.ceil(Math.sqrt(unlockedCount));
            const spacingX = 300;
            const spacingY = 250;
            unlockedNodes.forEach((node, index) => {
              node.position = {
                x: (index % cols) * spacingX + 100,
                y: Math.floor(index / cols) * spacingY + 100,
              };
            });
            break;
          }
            
          case 'hierarchical': {
            const currentEdges = edges;
            const incomingCount = new Map<string, number>();
            const adjacency = new Map<string, string[]>();
            
            layoutedNodes.forEach(n => {
              incomingCount.set(n.id, 0);
              adjacency.set(n.id, []);
            });
            
            currentEdges.forEach(e => {
              incomingCount.set(e.target, (incomingCount.get(e.target) || 0) + 1);
              const children = adjacency.get(e.source) || [];
              children.push(e.target);
              adjacency.set(e.source, children);
            });
            
            const layers = new Map<string, number>();
            const roots = layoutedNodes.filter(n => (incomingCount.get(n.id) || 0) === 0);
            if (roots.length === 0) roots.push(layoutedNodes[0]);
            
            const queue: Array<{ id: string; layer: number }> = roots.map(r => ({ id: r.id, layer: 0 }));
            const visited = new Set<string>();
            
            while (queue.length > 0) {
              const { id, layer } = queue.shift()!;
              if (visited.has(id)) continue;
              visited.add(id);
              layers.set(id, layer);
              
              const children = adjacency.get(id) || [];
              children.forEach(childId => {
                if (!visited.has(childId)) {
                  queue.push({ id: childId, layer: layer + 1 });
                }
              });
            }
            
            layoutedNodes.forEach(n => {
              if (!visited.has(n.id)) {
                layers.set(n.id, 0);
              }
            });
            
            const layerGroups = new Map<number, string[]>();
            layers.forEach((layer, id) => {
              const group = layerGroups.get(layer) || [];
              group.push(id);
              layerGroups.set(layer, group);
            });
            
            const spacingY = 250;
            const spacingX = 300;
            layerGroups.forEach((nodeIds, layer) => {
              const totalWidth = (nodeIds.length - 1) * spacingX;
              const startX = 500 - totalWidth / 2;
              nodeIds.forEach((nodeId, idx) => {
                const node = layoutedNodes.find(n => n.id === nodeId);
                if (node && !isNodeLocked(node)) {
                  node.position = {
                    x: startX + idx * spacingX,
                    y: layer * spacingY + 100,
                  };
                }
              });
            });
            break;
          }
            
          default: {
            // Advanced force-directed layout (Fruchterman-Reingold inspired)
            const NODE_WIDTH = 280;
            const NODE_HEIGHT = 160;
            const NODE_PADDING = Math.round(10 + (spacing / 100) * 110);
            
            const spacingFactor = 0.6 + (spacing / 100) * 1.0;
            const k = Math.sqrt((NODE_WIDTH + NODE_PADDING) * (NODE_HEIGHT + NODE_PADDING) * nodeCount) / Math.sqrt(nodeCount) * spacingFactor;
            const IDEAL_EDGE_LENGTH = Math.max(k, NODE_WIDTH + NODE_PADDING + 40);
            
            const ITERATIONS = 500;
            const INITIAL_TEMPERATURE = IDEAL_EDGE_LENGTH * 2;
            const COOLING_FACTOR = 0.95;
            const GRAVITY = 0.02;
            
            const currentEdges = edges;
            const edgePairs = currentEdges.map(e => ({
              source: e.source,
              target: e.target,
            }));
            
            const degree = new Map<string, number>();
            layoutedNodes.forEach(n => degree.set(n.id, 0));
            edgePairs.forEach(({ source, target }) => {
              degree.set(source, (degree.get(source) || 0) + 1);
              degree.set(target, (degree.get(target) || 0) + 1);
            });
            
            const positions: { [id: string]: { x: number; y: number } } = {};
            const forces: { [id: string]: { x: number; y: number } } = {};
            
            const sortedByDegree = [...layoutedNodes].sort((a, b) => 
              (degree.get(b.id) || 0) - (degree.get(a.id) || 0)
            );
            
            const lockedNodeIds = new Set(
              layoutedNodes.filter(n => isNodeLocked(n)).map(n => n.id)
            );
            
            const spiralSpacing = IDEAL_EDGE_LENGTH * 0.8;
            let unlockIdx = 0;
            sortedByDegree.forEach((node) => {
              if (lockedNodeIds.has(node.id)) {
                positions[node.id] = { x: node.position.x - 500, y: node.position.y - 400 };
              } else {
                if (unlockIdx === 0) {
                  positions[node.id] = { x: 0, y: 0 };
                } else {
                  const angle = unlockIdx * 2.4;
                  const radius = spiralSpacing * Math.sqrt(unlockIdx);
                  positions[node.id] = {
                    x: radius * Math.cos(angle),
                    y: radius * Math.sin(angle),
                  };
                }
                unlockIdx++;
              }
              forces[node.id] = { x: 0, y: 0 };
            });
            
            let temperature = INITIAL_TEMPERATURE;
            
            for (let iter = 0; iter < ITERATIONS; iter++) {
              layoutedNodes.forEach(n => {
                forces[n.id] = { x: 0, y: 0 };
              });
              
              // Repulsive forces
              for (let i = 0; i < nodeCount; i++) {
                for (let j = i + 1; j < nodeCount; j++) {
                  const idA = layoutedNodes[i].id;
                  const idB = layoutedNodes[j].id;
                  const posA = positions[idA];
                  const posB = positions[idB];
                  
                  let dx = posA.x - posB.x;
                  let dy = posA.y - posB.y;
                  let dist = Math.sqrt(dx * dx + dy * dy);
                  
                  if (dist < 0.1) {
                    dx = (Math.random() - 0.5) * 20;
                    dy = (Math.random() - 0.5) * 20;
                    dist = Math.sqrt(dx * dx + dy * dy);
                  }
                  
                  const overlapX = Math.max(0, (NODE_WIDTH + NODE_PADDING) - Math.abs(dx));
                  const overlapY = Math.max(0, (NODE_HEIGHT + NODE_PADDING) - Math.abs(dy));
                  
                  let repForce: number;
                  if (overlapX > 0 && overlapY > 0) {
                    repForce = (k * k) / Math.max(dist, 1) * 3;
                  } else {
                    repForce = (k * k) / dist;
                  }
                  
                  const fx = (dx / dist) * repForce;
                  const fy = (dy / dist) * repForce;
                  
                  forces[idA].x += fx;
                  forces[idA].y += fy;
                  forces[idB].x -= fx;
                  forces[idB].y -= fy;
                }
              }
              
              // Attractive forces
              edgePairs.forEach(({ source, target }) => {
                if (!positions[source] || !positions[target]) return;
                
                const posA = positions[source];
                const posB = positions[target];
                
                const dx = posB.x - posA.x;
                const dy = posB.y - posA.y;
                const dist = Math.sqrt(dx * dx + dy * dy);
                
                if (dist < 0.1) return;
                
                const attForce = (dist * dist) / (k * 3);
                
                const fx = (dx / dist) * attForce;
                const fy = (dy / dist) * attForce;
                
                forces[source].x += fx;
                forces[source].y += fy;
                forces[target].x -= fx;
                forces[target].y -= fy;
              });
              
              // Gravity
              layoutedNodes.forEach(node => {
                const pos = positions[node.id];
                const dist = Math.sqrt(pos.x * pos.x + pos.y * pos.y);
                if (dist > 0) {
                  const gravityForce = GRAVITY * dist * (1 + (degree.get(node.id) || 0) * 0.1);
                  forces[node.id].x -= (pos.x / dist) * gravityForce;
                  forces[node.id].y -= (pos.y / dist) * gravityForce;
                }
              });
              
              // Apply forces
              layoutedNodes.forEach(node => {
                if (lockedNodeIds.has(node.id)) return;
                
                const f = forces[node.id];
                const fMag = Math.sqrt(f.x * f.x + f.y * f.y);
                
                if (fMag > 0) {
                  const displacement = Math.min(fMag, temperature);
                  positions[node.id].x += (f.x / fMag) * displacement;
                  positions[node.id].y += (f.y / fMag) * displacement;
                }
              });
              
              temperature *= COOLING_FACTOR;
              if (temperature < 0.5) break;
            }
            
            // Post-processing: overlap removal
            for (let pass = 0; pass < 50; pass++) {
              let hasOverlap = false;
              
              for (let i = 0; i < nodeCount; i++) {
                for (let j = i + 1; j < nodeCount; j++) {
                  const idA = layoutedNodes[i].id;
                  const idB = layoutedNodes[j].id;
                  const aLocked = lockedNodeIds.has(idA);
                  const bLocked = lockedNodeIds.has(idB);
                  
                  if (aLocked && bLocked) continue;
                  
                  const posA = positions[idA];
                  const posB = positions[idB];
                  
                  const dx = posA.x - posB.x;
                  const dy = posA.y - posB.y;
                  
                  const minDistX = NODE_WIDTH + NODE_PADDING;
                  const minDistY = NODE_HEIGHT + NODE_PADDING;
                  
                  const overlapX = minDistX - Math.abs(dx);
                  const overlapY = minDistY - Math.abs(dy);
                  
                  if (overlapX > 0 && overlapY > 0) {
                    hasOverlap = true;
                    
                    if (overlapX < overlapY) {
                      const shift = overlapX + 5;
                      if (aLocked) {
                        posB.x += dx >= 0 ? -shift : shift;
                      } else if (bLocked) {
                        posA.x += dx >= 0 ? shift : -shift;
                      } else {
                        const halfShift = shift / 2;
                        if (dx >= 0) {
                          posA.x += halfShift;
                          posB.x -= halfShift;
                        } else {
                          posA.x -= halfShift;
                          posB.x += halfShift;
                        }
                      }
                    } else {
                      const shift = overlapY + 5;
                      if (aLocked) {
                        posB.y += dy >= 0 ? -shift : shift;
                      } else if (bLocked) {
                        posA.y += dy >= 0 ? shift : -shift;
                      } else {
                        const halfShift = shift / 2;
                        if (dy >= 0) {
                          posA.y += halfShift;
                          posB.y -= halfShift;
                        } else {
                          posA.y -= halfShift;
                          posB.y += halfShift;
                        }
                      }
                    }
                  }
                }
              }
              
              if (!hasOverlap) break;
            }
            
            // Center the graph
            let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
            layoutedNodes.forEach(node => {
              const pos = positions[node.id];
              minX = Math.min(minX, pos.x);
              minY = Math.min(minY, pos.y);
              maxX = Math.max(maxX, pos.x);
              maxY = Math.max(maxY, pos.y);
            });
            
            const graphCenterX = (minX + maxX) / 2;
            const graphCenterY = (minY + maxY) / 2;
            
            layoutedNodes.forEach(node => {
              if (lockedNodeIds.has(node.id)) return;
              node.position = {
                x: positions[node.id].x - graphCenterX + 500,
                y: positions[node.id].y - graphCenterY + 400,
              };
            });
            break;
          }
        }
        
        return layoutedNodes;
      });
      
      setTimeout(() => {
        fitView({ padding: 0.2, duration: 800 });
        setEdges((currentEdges) =>
          currentEdges.map((edge) => ({
            ...edge,
            type: 'floating',
            animated: false,
            style: { ...edge.style, stroke: edgeColor, strokeWidth: 2.5 },
            markerEnd: { type: MarkerType.ArrowClosed, color: edgeColor },
          }))
        );
        setIsApplyingLayout(false);
        markDirty();
      }, 100);
    }, 50);
  }, [edges, setNodes, setEdges, fitView, edgeColor, markDirty]);

  // Toolbar actions
  const handleHome = useCallback(() => {
    window.location.href = '/';
  }, []);

  // ─── Save vs Publish refactor (Step C wiring) ────────────────────────────
  //
  // Save is now a *silent, in-place* persistence of the current draft. No
  // modal, no version field, no rename. The user just clicks Save and the
  // toolbar shows transient feedback. Renaming an ontology is now a separate
  // explicit gesture (handleRename → RenameDialog → PATCH /:id).
  //
  // For brand-new ontologies that don't yet have an `ontologyId` (rare —
  // Start-from-Prompt and Start-from-KB both seed one — but possible if a
  // user lands on /editor with a manually-built graph), we fall back to the
  // legacy `POST /save` flow with a default name so the next click writes
  // through to a real id.
  const handleSave = useCallback(async () => {
    const reactFlowInstance = document.querySelector('.react-flow__viewport');
    let viewport = { x: 0, y: 0, zoom: 1 };
    
    if (reactFlowInstance) {
      const transform = reactFlowInstance.getAttribute('style');
      const match = transform?.match(/translate\(([^,]+)px,\s*([^)]+)px\)\s*scale\(([^)]+)\)/);
      if (match) {
        viewport = {
          x: parseFloat(match[1]),
          y: parseFloat(match[2]),
          zoom: parseFloat(match[3]),
        };
      }
    }

    const payload = {
      viewport,
      nodes: nodes.map((node) => ({
        node_id: node.id,
        label: (node.data as ConceptNodeData).label,
        definition: (node.data as ConceptNodeData).definition || undefined,
        properties: (node.data as ConceptNodeData).properties || [],
        position_x: node.position.x,
        position_y: node.position.y,
        metadata: {
          type: node.type,
          locked: (node.data as ConceptNodeData).locked || false,
          // A9: persist who created/last-edited the concept ('ai' | 'user').
          origin: (node.data as ConceptNodeData).origin || 'ai',
        },
        // Provenance columns (migration 039) — persisted as first-class DB
        // columns, not inside metadata.
        provenance: (node.data as ConceptNodeData).provenance ?? 'ai',
        created_by_name: (node.data as ConceptNodeData).createdByName ?? null,
        updated_by_name: (node.data as ConceptNodeData).updatedByName ?? null,
        corrected_at: (node.data as ConceptNodeData).correctedAt ?? null,
      })),
      edges: edges.map((edge) => {
        const edgeData = (edge.data ?? {}) as ConceptEdgeData;
        const fallbackLabel =
          typeof edge.label === 'string' ? edge.label : 'relates to';
        // Convert properties array → { name: type } map for the backend's
        // current persistence shape, while keeping the rich payload in
        // metadata so we can restore it lossless-ly on reload.
        const propertiesMap: Record<string, string> = {};
        (edgeData.properties || []).forEach((p) => {
          if (p?.name) propertiesMap[p.name] = p.type ?? 'str';
        });
        return {
          edge_id: edge.id,
          source_node_id: edge.source,
          target_node_id: edge.target,
          label: edgeData.label || fallbackLabel,
          relationship_type:
            (edgeData.relationshipType as string) ||
            edgeData.label ||
            fallbackLabel,
          properties: propertiesMap,
          metadata: {
            description: edgeData.description || '',
            bidirectional: !!edgeData.bidirectional,
            properties: edgeData.properties || [],
            origin: edgeData.origin || 'ai',
            rationale: edgeData.rationale || '',
          },
          // Provenance columns (migration 039) — first-class DB columns.
          provenance: edgeData.provenance ?? 'ai',
          created_by_name: edgeData.createdByName ?? null,
          updated_by_name: edgeData.updatedByName ?? null,
          corrected_at: edgeData.correctedAt ?? null,
        };
      }),
      metadata: {
        domain: 'General Knowledge',
      },
    };

    // Cancel any pending revert-to-idle from a previous click — otherwise the
    // checkmark from the prior save could disappear mid-animation.
    if (saveStatusTimerRef.current) {
      clearTimeout(saveStatusTimerRef.current);
      saveStatusTimerRef.current = null;
    }
    setSaveStatus('saving');

    try {
      if (ontologyId) {
        // Existing ontology → non-destructive in-place draft save. Keeps the
        // same id and name, and (crucially) does NOT bump the version number.
        // Publish bumps; Save just persists.
        await ontologyApi.saveDraft(ontologyId, payload);
      } else {
        // No id yet — create flow. Only the name is passed: there is no version
        // number to send (a draft has none) and no save label to invent.
        const saved = await ontologyApi.saveOntology({
          name: ontologyName || 'Untitled Ontology',
          ...payload,
          ...(sourceDocumentIds.length > 0 ? { source_document_ids: sourceDocumentIds } : {}),
        });
        if (saved?.id) {
          setOntologyId(saved.id);
          // Replace history entry so the back button doesn't go to the
          // now-stale previous editor URL. Using `replace: true` avoids a
          // navigation that would re-trigger the load effect (we already
          // have the latest graph in memory).
          navigate(`/editor/${saved.id}`, { replace: true });
        }
      }
      // Clear the unsaved flag on all edges — the backend has confirmed the write.
      setEdges((eds) =>
        eds.map((edge) => {
          const d = edge.data as ConceptEdgeData | undefined;
          if (!d?.unsaved) return edge;
          return { ...edge, data: { ...d, unsaved: false } };
        }),
      );
      // Mark the graph as clean.
      setIsDirty(false);
      // Briefly flash the "saved" state, then settle back to idle so a
      // future save click feels responsive again.
      setSaveStatus('saved');
      saveStatusTimerRef.current = setTimeout(() => {
        setSaveStatus('idle');
        saveStatusTimerRef.current = null;
      }, 1800);
    } catch (err) {
      console.error('Failed to save draft:', err);
      // Stay in error state until the user clicks again — they need to know
      // their work didn't land. Tooltip on the button explains "click to retry".
      setSaveStatus('error');
    }
  }, [nodes, edges, ontologyId, ontologyName, navigate, sourceDocumentIds, setEdges]);

  // Clear any pending save-status timer on unmount to avoid setting state on
  // an unmounted component (e.g. user navigates away mid-animation).
  useEffect(() => {
    return () => {
      if (saveStatusTimerRef.current) {
        clearTimeout(saveStatusTimerRef.current);
      }
    };
  }, []);

  // Timer ref for debouncing auto-saves triggered by proposal acceptance.
  // We debounce by 300 ms so accepting several proposals in quick succession
  // only fires one save round-trip.
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Triggered by ChatInterface after a proposal is accepted.
   * Only fires when the ontology is already persisted (ontologyId is set);
   * a brand-new unsaved ontology needs a name from the user first.
   * Debounced so rapid back-to-back acceptances coalesce into a single save.
   */
  const handleAutoSaveAfterAccept = useCallback(() => {
    if (!ontologyId) return;
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      autoSaveTimerRef.current = null;
      handleSave();
    }, 300);
  }, [ontologyId, handleSave]);

  // Clear the debounce timer on unmount.
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
  }, []);

  const handleExport = useCallback(() => {
    setIsExportDialogOpen(true);
  }, []);

  // Reflect the ontology name in the browser tab so it is findable even when
  // the canvas is scrolled/zoomed (A16).
  useEffect(() => {
    if (!ontologyName) return;
    const previous = document.title;
    document.title = `${ontologyName} · Ontology`;
    return () => {
      document.title = previous;
    };
  }, [ontologyName]);

  // Rename + re-describe the ontology (PATCH /api/ontologies/:id). Save persists
  // the graph and Publish snapshots a version; editing the name/description is a
  // separate, explicit gesture surfaced by the toolbar "Edit details" action.
  const handleSaveDetails = useCallback(
    async ({ name, description }: { name: string; description: string }) => {
      if (!ontologyId) return;
      setIsSavingDetails(true);
      try {
        await ontologyApi.updateOntology(ontologyId, { name, description });
        setOntologyName(name);
        setOntologyDescription(description);
        setIsDetailsDialogOpen(false);
      } finally {
        setIsSavingDetails(false);
      }
    },
    [ontologyId],
  );

  const handleResetView = useCallback(() => {
    fitView({ padding: 0.2, duration: 800 });
  }, [fitView]);

  // Close inspector
  const handleCloseInspector = useCallback(() => {
    setIsInspectorOpen(false);
  }, []);

  // Toggle node browser — mutually exclusive with the inspector left slot.
  const handleToggleNodeBrowser = useCallback(() => {
    setIsNodeBrowserOpen(prev => {
      if (!prev) {
        // Opening browser: clear any selection so the inspector slot is free.
        setIsInspectorOpen(false);
        setSelectedNode(null);
        setSelectedEdge(null);
      }
      return !prev;
    });
  }, []);

  // Called when the user clicks a row in the Node Browser.
  // Closes the browser, focuses the node on canvas, then lets the
  // inspector auto-open via the selectedNode useEffect.
  const handleSelectNodeFromBrowser = useCallback((nodeId: string) => {
    setIsNodeBrowserOpen(false);
    focusNode(nodeId);
    const node = getNode(nodeId);
    if (node) {
      setSelectedNode(node);
      setSelectedEdge(null);
    }
  }, [focusNode, getNode]);

  // Toggle AI panel
  const handleToggleAiPanel = useCallback(() => {
    setIsAiPanelOpen(prev => !prev);
  }, []);

  // Stable node/edge type maps. React Flow uses referential equality on
  // nodeTypes/edgeTypes to decide whether to remount all node components.
  // NEVER recreate these objects on every render or when callbacks change.
  // Instead, callbacks are passed via node.data (see below).
  const nodeTypes = useMemo(() => ({ concept: ConceptNode }), []);
  const edgeTypes = useMemo(() => ({ floating: FloatingEdge }), []);

  // One-shot auto-layout after loading: if the load effect detected that no
  // positions were saved, it sets autoLayoutPendingRef. We consume it here
  // once nodes are non-empty and edges have also arrived in state.
  useEffect(() => {
    if (!autoLayoutPendingRef.current) return;
    if (nodes.length === 0) return;
    autoLayoutPendingRef.current = false;
    // Short delay to let ReactFlow measure node dimensions before force layout.
    setTimeout(() => {
      handleApplyLayout('force', 30);
    }, 200);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally fires only when nodes first populate
  }, [nodes.length]);

  // Stable MiniMap helpers — avoid recreating on every render.
  const miniMapNodeColor = useCallback((node: Node) => {
    const data = node.data as ConceptNodeData;
    return data.locked ? 'var(--cds-support-warning)' : 'var(--cds-interactive-01)';
  }, []);
  const miniMapStyle = useMemo(() => ({ backgroundColor: 'var(--cds-layer-02)' }), []);

  // Inject stable callback references into every node's `data` so the
  // memoized ConceptNode can read them without needing a wrapper component.
  // We use refs to hold the latest callbacks to avoid recreating all node
  // data objects whenever a handler identity changes (which would defeat memo).
  const callbacksRef = useRef({
    onExpandConcept: handleExpandConcept,
    onDeleteNode: handleDeleteNode,
    onLockNode: handleLockNode,
    onUnlockNode: handleUnlockNode,
    onLinkConcept: handleLinkConcept,
  });
  useEffect(() => {
    callbacksRef.current = {
      onExpandConcept: handleExpandConcept,
      onDeleteNode: handleDeleteNode,
      onLockNode: handleLockNode,
      onUnlockNode: handleUnlockNode,
      onLinkConcept: handleLinkConcept,
    };
  }, [handleExpandConcept, handleDeleteNode, handleLockNode, handleUnlockNode, handleLinkConcept]);

  // Stable callbacks that delegate to callbacksRef — their identity never
  // changes so they can be placed in node.data without triggering re-renders.
  const stableExpandConcept = useCallback((id: string) => callbacksRef.current.onExpandConcept(id), []);
  const stableDeleteNode = useCallback((id: string) => callbacksRef.current.onDeleteNode(id), []);
  const stableLockNode = useCallback((id: string) => callbacksRef.current.onLockNode(id), []);
  const stableUnlockNode = useCallback((id: string) => callbacksRef.current.onUnlockNode(id), []);
  const stableLinkConcept = useCallback((id: string) => callbacksRef.current.onLinkConcept(id), []);

  // Patch node data with stable callbacks so ConceptNode can read them.
  // This runs as a derived value — nodes already flow through setNodes,
  // so we enrich them just before passing to ReactFlow.
  const nodesWithCallbacks = useMemo(
    () =>
      nodes.map((node) => {
        // Only patch if not already patched (avoid creating new objects on
        // every render when data already has the callbacks).
        const d = node.data as any;
        if (
          d.onExpandConcept === stableExpandConcept &&
          d.onDeleteNode === stableDeleteNode &&
          d.onLockNode === stableLockNode &&
          d.onUnlockNode === stableUnlockNode &&
          d.onLinkConcept === stableLinkConcept &&
          d.isReadOnly === isReadOnly
        ) {
          return node;
        }
        return {
          ...node,
          draggable: !isReadOnly,
          data: {
            ...node.data,
            onExpandConcept: stableExpandConcept,
            onDeleteNode: stableDeleteNode,
            onLockNode: stableLockNode,
            onUnlockNode: stableUnlockNode,
            onLinkConcept: stableLinkConcept,
            isReadOnly,
          },
        };
      }),
    [nodes, stableExpandConcept, stableDeleteNode, stableLockNode, stableUnlockNode, stableLinkConcept, isReadOnly],
  );

  return (
    <div className={`ontology-editor ${isConnecting ? 'is-connecting' : ''}`}>
      {/* Unified Toolbar — full width at top */}
      <UnifiedToolbar
        onHome={handleHome}
        onAddConcept={isReadOnly ? () => {} : handleAddConcept}
        onAddPapers={isReadOnly || !ontologyId ? undefined : () => setAddPapersOpen(true)}
        onLockAll={handleLockAll}
        onUnlockAll={handleUnlockAll}
        onSave={isReadOnly ? () => {} : handleSave}
        onPublish={
          isReadOnly || !ontologyId ? undefined : () => setIsPublishDialogOpen(true)
        }
        onExport={handleExport}
        onEditDetails={
          isReadOnly || !ontologyId ? undefined : () => setIsDetailsDialogOpen(true)
        }
        ontologyName={ontologyName}
        onShowSources={ontologyId ? () => setIsSourcesOpen(true) : undefined}
        onZoomIn={() => zoomIn({ duration: 300 })}
        onZoomOut={() => zoomOut({ duration: 300 })}
        onResetView={handleResetView}
        theme={theme}
        onToggleTheme={onToggleTheme}
        onApplyLayout={handleApplyLayout}
        currentAlgorithm={currentLayout}
        isApplyingLayout={isApplyingLayout}
        isNodeBrowserOpen={isNodeBrowserOpen}
        onToggleNodeBrowser={handleToggleNodeBrowser}
        isAiPanelOpen={isAiPanelOpen}
        onToggleAiPanel={handleToggleAiPanel}
        isReadOnly={isReadOnly}
        versionLabel={versionLabelText}
        onShowVersionHistory={
          ontologyId ? () => setIsVersionHistoryOpen(true) : undefined
        }
        saveStatus={saveStatus}
      />

      {/* Read-only banner. Two distinct read-only modes share the same UI:
          - viewing a published *snapshot* of one of your own ontologies
            (Step C): we point users back to the live draft;
          - viewing an ontology that was *shared* with you. */}
      {isReadOnly && viewingSnapshot && (
        <div className="ontology-editor__readonly-banner">
          Viewing published version <strong>{viewingSnapshot.version_number}</strong>
          {viewingSnapshot.published_at
            ? ` (${new Date(viewingSnapshot.published_at).toLocaleDateString()})`
            : ''}
          {' — read-only. '}
          <a
            href={`/editor/${ontologyId}`}
            onClick={(e) => {
              e.preventDefault();
              navigate(`/editor/${ontologyId}`);
            }}
          >
            Back to current draft
          </a>
        </div>
      )}
      {isReadOnly && !viewingSnapshot && (
        <div className="ontology-editor__readonly-banner">
          This ontology is shared with you in read-only mode. You can view and explore but not edit.
        </div>
      )}
      {isDirty && !isReadOnly && (
        <div className="ontology-editor__unsaved-banner">
          Unsaved changes — <button className="ontology-editor__unsaved-banner-save" onClick={handleSave}>Save now</button>
        </div>
      )}

      {/* Body: Left Inspector | Canvas | Right AI Panel */}
      <div className="ontology-editor__body">
        {/* Left Panel — sliding drawer shared by Inspector and Node Browser.
            Both panels are mutually exclusive — opening one closes the other.
            Node Browser: opened via toolbar button.
            Inspector: opens automatically when a node/edge is selected. */}
        <div
          data-resizable-panel="inspector"
          className={`inspector-panel ${
            (isInspectorOpen && (selectedNode || selectedEdge)) || isNodeBrowserOpen
              ? 'inspector-panel--open'
              : ''
          }`}
          style={inspectorResize.width ? { width: inspectorResize.width } : undefined}
        >
          {isNodeBrowserOpen && (
            <NodeBrowserPanel
              nodes={nodes}
              edges={edges}
              onSelectNode={handleSelectNodeFromBrowser}
              onClose={handleToggleNodeBrowser}
            />
          )}
          {!isNodeBrowserOpen && selectedNode && !selectedEdge && (
            <NodeDetailsPanel
              node={selectedNode}
              onUpdateNode={handleUpdateNode}
              onExpandConcept={handleExpandConcept}
              onLinkConcept={handleLinkConcept}
              onLockNode={handleLockNode}
              onUnlockNode={handleUnlockNode}
              onClose={handleCloseInspector}
              isReadOnly={isReadOnly}
            />
          )}
          {!isNodeBrowserOpen && selectedEdge && (
            <EdgeDetailsPanel
              edge={selectedEdge}
              sourceNode={nodes.find((n) => n.id === selectedEdge.source)}
              targetNode={nodes.find((n) => n.id === selectedEdge.target)}
              onUpdateEdge={handleUpdateEdge}
              onDeleteEdge={handleDeleteEdge}
              onClose={handleCloseInspector}
              isReadOnly={isReadOnly}
            />
          )}
        </div>
        {/* Resize handle — visible whenever the left panel is open. */}
        {((isInspectorOpen && (selectedNode || selectedEdge)) || isNodeBrowserOpen) && (
          <ResizeHandle
            className="panel-resize-handle--inspector"
            style={{ left: `${inspectorResize.width ?? 340}px` }}
            onPointerDown={inspectorResize.onHandlePointerDown}
            ariaLabel="Resize inspector panel"
          />
        )}

        {/* Center — Graph Canvas */}
        <div className="ontology-editor__canvas">
          {isLoadingOntology ? (
            <div className="ontology-editor__loading">
              Loading ontology...
            </div>
          ) : (
            <ReactFlow
              nodes={nodesWithCallbacks}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              onConnectStart={onConnectStart}
              onConnectEnd={onConnectEnd}
              isValidConnection={isValidConnection}
              onEdgeDoubleClick={onEdgeDoubleClick}
              onSelectionChange={onSelectionChange}
              nodeTypes={nodeTypes}
              edgeTypes={edgeTypes}
              connectionMode={ConnectionMode.Loose}
              defaultEdgeOptions={{ type: 'floating' }}
              edgesFocusable
              edgesReconnectable
              minZoom={0.01}
              maxZoom={4}
              fitView
              attributionPosition="bottom-left"
              style={{ backgroundColor: 'var(--cds-layer-01)' }}
            >
              <Background color="var(--cds-border-subtle-01)" />
              <MiniMap 
                nodeColor={miniMapNodeColor}
                style={miniMapStyle}
              />
            </ReactFlow>
          )}

          {/* Stats Panel */}
          <div className="ontology-editor__stats">
            <div className="ontology-editor__stats-name">
              {ontologyName}
            </div>
            <div className="ontology-editor__stats-counts">
              <strong>{nodes.length}</strong> Concepts • <strong>{edges.length}</strong> Relations
            </div>
          </div>
        </div>

        {/* A7: drag to resize the AI panel (only while it's open). */}
        {isAiPanelOpen && (
          <ResizeHandle
            className="panel-resize-handle--ai"
            onPointerDown={aiResize.onHandlePointerDown}
            ariaLabel="Resize AI assistant panel"
          />
        )}

        {/* Right Panel — AI Assistant (collapsible) */}
        <div
          data-resizable-panel="ai"
          className={`ai-panel ${!isAiPanelOpen ? 'ai-panel--collapsed' : ''}`}
          style={
            isAiPanelOpen && aiResize.width
              ? { width: aiResize.width, transition: aiResize.isResizing ? 'none' : undefined }
              : undefined
          }
        >
          <div
            className="ai-panel__content"
            style={isAiPanelOpen && aiResize.width ? { minWidth: aiResize.width } : undefined}
          >
            <AiToolsPanel
              ontology={{
                id: ontologyId,
                nodes,
                edges,
                domain: 'General Knowledge',
              }}
              contextData={selectedNodes.length > 0 ? {
                nodes: selectedNodes,
                edges: edges.filter(e =>
                  selectedNodes.some(n => n.id === e.source || n.id === e.target)
                ),
              } : undefined}
              onCitationClick={focusNode}
              selectedNodes={selectedNodes}
              allNodes={nodes}
              allEdges={edges}
              userId={user?.id || ''}
              onAddEdges={handleAddAiEdges}
              onAddNode={handleAddAiNode}
              onAddEdge={handleAddAiEdge}
              onUpdateNode={handleUpdateNodeFromProposal}
              onProposalAccepted={handleAutoSaveAfterAccept}
              onDeleteNodes={(ids) => ids.forEach(handleDeleteNode)}
            />
          </div>
        </div>
      </div>

      {/* A1: Add papers to this ontology (extend from more KB documents) */}
      {ontologyId && (
        <AddPapersModal
          open={addPapersOpen}
          onClose={() => setAddPapersOpen(false)}
          ontologyId={ontologyId}
          onExtended={handleExtendResult}
        />
      )}

      {/* Concept Linker Modal */}
      <ConceptLinker
        isOpen={isLinkerOpen}
        onClose={() => {
          setIsLinkerOpen(false);
          setLinkSourceNode(null);
        }}
        sourceConcept={linkSourceNode}
        availableConcepts={nodes}
        existingEdges={edges}
        onCreateLink={handleCreateLink}
      />

      {/* Concept Expander Modal */}
      <ConceptExpander
        isOpen={isExpanderOpen}
        onClose={() => {
          setIsExpanderOpen(false);
          setExpandSourceNode(null);
        }}
        concept={expandSourceNode}
        allNodes={nodes}
        allEdges={edges}
        onAddConcepts={handleAddConcepts}
      />

      {/* Save is silent (no dialog). The legacy SaveOntologyDialog was DELETED on 2026-08-19: it had
          been unmounted for weeks and still sent `saveLabel` where the API reads `save_label`, so it
          was a working-looking trap for whoever remounted it. Renaming lives in
          OntologyDetailsDialog below. */}

      {/* Ontology sources (A17): which KB docs generated this ontology */}
      <OntologySourcesModal
        isOpen={isSourcesOpen}
        ontologyId={ontologyId}
        onClose={() => setIsSourcesOpen(false)}
      />

      {/* Ontology details (rename + description) */}
      <OntologyDetailsDialog
        isOpen={isDetailsDialogOpen}
        initialName={ontologyName}
        initialDescription={ontologyDescription}
        isSaving={isSavingDetails}
        onSave={handleSaveDetails}
        onClose={() => setIsDetailsDialogOpen(false)}
      />

      {/* Export Dialog */}
      <ExportDialog
        isOpen={isExportDialogOpen}
        onClose={() => setIsExportDialogOpen(false)}
        nodes={nodes}
        edges={edges}
        ontologyName={ontologyName}
      />

      {/* Publish Version Dialog (Step C): assigns the number to the draft being
          published, freezes it, and forks a fresh unnumbered draft. */}
      {/* The pill and the publish baseline both come from the version graph, and
          usePublishVersion invalidates it, so there is nothing to mirror in local
          state after a publish. */}
      <PublishVersionDialog
        isOpen={isPublishDialogOpen}
        onClose={() => setIsPublishDialogOpen(false)}
        ontologyId={ontologyId}
      />

      {/* Version History Panel (Step C) — read-only timeline of every
          published version, plus the current draft. Reachable from the
          version pill in the toolbar. */}
      <VersionHistoryPanel
        isOpen={isVersionHistoryOpen}
        onClose={() => setIsVersionHistoryOpen(false)}
        ontologyId={ontologyId}
        canPublish={!isReadOnly && !!ontologyId}
        onPublishClick={() => setIsPublishDialogOpen(true)}
      />
    </div>
  );
}

/**
 * OntologyEditorPage Component with ReactFlowProvider
 */
export default function OntologyEditorPage({ theme, onToggleTheme }: OntologyEditorPageProps) {
  return (
    <ReactFlowProvider>
      <OntologyEditorPageContent theme={theme} onToggleTheme={onToggleTheme} />
    </ReactFlowProvider>
  );
}

// Made with Bob
